import { useRef, useEffect, useState, useCallback } from 'react';

import { cn } from '@/components/TailwindComponents/lib/utils';

export interface SignatureCanvasProps {
  value?: string;
  onChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
  className?: string;
  height?: number;
  placeholder?: string;
}

export const SignatureCanvas = ({
  value,
  onChange,
  label,
  disabled = false,
  className,
  height = 150,
  placeholder = 'Sign here',
}: SignatureCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const isDarkMode =
    typeof window !== 'undefined' &&
    document.documentElement.classList.contains('dark');
  const canvasBackgroundColor = isDarkMode ? '#1f2937' : '#ffffff';
  const canvasStrokeColor = isDarkMode ? '#f9fafb' : '#000000';

  // Initialize canvas and load existing signature
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = height;

    // Set drawing styles
    ctx.strokeStyle = canvasStrokeColor;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Fill with theme-aware background
    ctx.fillStyle = canvasBackgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Load existing signature if present
    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        setHasSignature(true);
      };
      img.src = value;
    }
  }, [value, height, canvasBackgroundColor, canvasStrokeColor]);

  const getCoordinates = useCallback(
    (
      e:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>,
    ) => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };

      const rect = canvas.getBoundingClientRect();

      if ('touches' in e) {
        return {
          x: e.touches[0].clientX - rect.left,
          y: e.touches[0].clientY - rect.top,
        };
      }

      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    },
    [],
  );

  const startDrawing = useCallback(
    (
      e:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>,
    ) => {
      if (disabled) return;

      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!ctx) return;

      const { x, y } = getCoordinates(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
      setIsDrawing(true);
    },
    [disabled, getCoordinates],
  );

  const draw = useCallback(
    (
      e:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>,
    ) => {
      if (!isDrawing || disabled) return;

      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!ctx) return;

      const { x, y } = getCoordinates(e);
      ctx.lineTo(x, y);
      ctx.stroke();
      setHasSignature(true);
    },
    [isDrawing, disabled, getCoordinates],
  );

  const stopDrawing = useCallback(() => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    ctx.closePath();
    setIsDrawing(false);

    // Save signature as base64 data URL
    if (canvas) {
      const dataUrl = canvas.toDataURL('image/png');
      onChange(dataUrl);
    }
  }, [isDrawing, onChange]);

  const clearSignature = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    // Clear and fill with theme-aware background
    ctx.fillStyle = canvasBackgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onChange('');
  }, [canvasBackgroundColor, onChange]);

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label && (
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
            {label}
          </label>
          {!disabled && hasSignature && (
            <span
              onClick={clearSignature}
              className="text-sm font-medium text-red-600 hover:text-red-700 transition-colors cursor-pointer"
            >
              Clear
            </span>
          )}
        </div>
      )}
      {!label && !disabled && hasSignature && (
        <div className="flex justify-end">
          <span
            onClick={clearSignature}
            className="text-sm font-medium text-red-600 hover:text-red-700 transition-colors cursor-pointer"
          >
            Clear
          </span>
        </div>
      )}
      <div
        className={cn(
          'relative border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        <canvas
          ref={canvasRef}
          className={cn(
            'w-full cursor-crosshair touch-none bg-white dark:bg-gray-800',
            disabled && 'cursor-not-allowed',
          )}
          style={{ height: `${height}px` }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        {!hasSignature && !disabled && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-gray-400 dark:text-gray-500 text-sm">
              {placeholder}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignatureCanvas;
